import React, { useState, useEffect } from 'react';

// Utility function to convert time strings to a standard 12-hour format
const convertTo12HourFormat = (time: string): string => {
  let hours = 0;
  let minutes = 0;

  if (time.includes(':')) {
    const [h, m] = time.split(':').map(Number);
    hours = h;
    minutes = m;
  } else {
    // Handle military time format
    hours = Math.floor(Number(time) / 100);
    minutes = Number(time) % 100;
  }

  if (hours >= 24 || minutes >= 60 || isNaN(hours) || isNaN(minutes)) {
    throw new Error('Invalid time');
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format

  // return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;

};

const generateDropdownOptions = (): string[] => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      // const formattedTime = `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
      const formattedTime = `${displayHour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')} ${period}`;
      options.push(formattedTime);
    }
  }
  return options;
};

interface CustomTimePickerProps {
  value: string;
  onChange: (value: string, property: string) => void;
  property: string;
  disable?: boolean;
  setError?: (value: string) => void;
  minValue?: string;
  minText?: string;
  maxValue?: string;
  maxText?: string;
  /**
   * Identifies this picker to the mandatory-field validation, which locates controls by
   * `data-field`. Also used as the input's `id`, so the caller's <label htmlFor> resolves.
   */
  fieldKey?: string;
  /**
   * Marks the input as invalid. Only the outline is drawn here — every caller already
   * renders its own message span for these fields, so drawing one would duplicate it.
   */
  error?: string;
}

const CustomTimePicker: React.FC<CustomTimePickerProps> = ({ value, onChange, property, disable, setError, minValue, minText ,maxValue, maxText, fieldKey, error }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rawInput, setRawInput] = useState(value);
  const options = generateDropdownOptions();
  const [formatError, setFormatError] = useState(false);

  useEffect(() => {
    setRawInput(value);
  }, [value]);

  const isTimeMinValid = (time: string, minValue?: string): boolean => {
    if (!minValue) return true;

    // Convert times to minutes for easy comparison
    const timeToMinutes = (timeStr: string) => {
      const [hour, minute] = timeStr.split(/[: ]/).map(Number);
      const isPM = timeStr.includes('PM');
      return (hour % 12 + (isPM ? 12 : 0)) * 60 + minute;
    };

    return timeToMinutes(time) >= timeToMinutes(minValue);
  };
  const isTimeMaxValid = (time: string, minValue?: string): boolean => {
    if (!maxValue) return true;

    // Convert times to minutes for easy comparison
    const timeToMinutes = (timeStr: string) => {
      const [hour, minute] = timeStr.split(/[: ]/).map(Number);
      const isPM = timeStr.includes('PM');
      return (hour % 12 + (isPM ? 12 : 0)) * 60 + minute;
    };

    return timeToMinutes(time) <= timeToMinutes(maxValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
     // Restrict input to digits and colons only
     if (!/^[0-9:]*$/.test(input)) {
        return;
      }
    setRawInput(input);
    setFormatError(false);
    setError && setError("");
    if (input === '') {
      onChange('', property);
      return;
    }

    if (/^[0-9:]*$/.test(input)) {
      if (input.includes(':')? input.length>=5 : input.length>=4 ) {
        try {
          setFormatError(false);
          setError && setError("");
          const formattedTime = convertTo12HourFormat(input);
          if(!isTimeMinValid(formattedTime, minValue)){
            setError && setError(`${maxText} should be greater than or equal to the ${minText}`);
          }
          if(!isTimeMaxValid(formattedTime, maxValue)){
            setError && setError(`${minText} should be less than or equal to the ${maxText}`);
          }
          setRawInput(formattedTime);
          onChange(formattedTime, property);
        } catch (err) {
          setFormatError(true);
          setError && setError("format error")
        }
      }
      else{
        setFormatError(true); 
        setError && setError("format error");
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (rawInput === '' || rawInput.endsWith('M')) {
        onChange('', property);
        setRawInput('');
      }
    } else {
        // Prevent additional typing after conversion
        if (/\d|:|AM|PM/.test(e.key) && /^\d{1,2}:\d{2} (AM|PM)$/.test(rawInput)) {
          e.preventDefault();
        }
      }
    
  };

  const handleDropdownSelect = (time: string) => {
    setFormatError(false);
    setError && setError("")
    setRawInput(time);
    onChange(time, property);
    setDropdownOpen(false);
    if(!isTimeMinValid(time, minValue)){
      setError && setError(`${maxText} should be greater than or equal to the ${minText}`);
    }
    if(!isTimeMaxValid(time, maxValue)){
      setError && setError(`${minText} should be less than or equal to the ${maxText}`);
    }
  };

  const handleInputFocus = () => {
    setDropdownOpen(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !relatedTarget.classList.contains('dropdown-item')) {
      setDropdownOpen(false);
    }
  };

  const handleDropdownMouseDown = (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
  };

  return (
    <div className="custom-time-picker" style={{ position: 'relative', width: '100%'}}>
      <input
        type="text"
        id={fieldKey}
        data-field={fieldKey}
        value={rawInput}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder="00:00"
        className= {`${disable?"form__input form__input--sm form__date--time form__date--disabled":"form__input form__input--sm form__date--time "}${error ? " form__date--error" : ""}`}
        autoComplete="off"
        onFocus={handleInputFocus}
        onClick={handleInputFocus}
        onBlur={handleInputBlur}
        disabled={disable}
      />
      {formatError && (
        <span className="form__error">
        {"Please enter valid time(HH:MM)"}
        </span>
        )}
      {dropdownOpen && (
        <ul
          className="dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '200px',
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #ccc',
            zIndex: 1000,
            listStyleType: 'none',
            padding: 0,
            margin: 0,
          }}
        >
          {options.map((option) => (
            <li
              key={option}
              onMouseDown={handleDropdownMouseDown}
              onClick={() => handleDropdownSelect(option)}
              className="dropdown-item"
              style={{ padding: '8px', cursor: 'pointer' }}
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomTimePicker;
